/**
 * The plays of a set of capsules, for the module they make up.
 *
 * A learner's play of a capsule is posted to the AppView by the player itself
 * (`app.bsky.feed.createPlay`, see `#/state/queries/create-play`), and this is the
 * other end of that wire — the same arrangement as a book's class report
 * (`#/state/queries/flashbook-class-plays`), pointed at capsules instead of pages.
 * One subject asked across *all* its learners, which is the only way a
 * leaderboard can exist: a teacher's phone has never seen a learner's answers.
 *
 * **Batched by uri, on purpose.** A module is six capsules or so, and the AppView
 * stores plays per capsule — so a module's board is one question with six capsule
 * uris in it, not six requests stitched together here. That is also what keeps the
 * endpoint small: a server only has to answer about plays it already holds, and
 * needs to know nothing about modules existing at all.
 *
 * Two details that are easy to get wrong and fail silently, both inherited from
 * the book's version of this query:
 *
 *  • **The host is the AppView, and it is called through `fetch`.** The endpoint
 *    is a Taderok extension that is not among the custom lexicons the frontend
 *    registers, so an `agent.app.bsky.feed.getCapsulePlays` call would throw for
 *    an unknown method rather than reporting anything useful.
 *  • **A deployment that has not been taught this method is not an error.** It
 *    answers 501 or 404, and both are reported as *no plays* so the board says
 *    "no one has finished this yet" instead of throwing at a learner. The feature
 *    switches itself on when the backend that implements the handler is deployed —
 *    and until then the board is what the dev preview draws (see
 *    `#/lib/moduleFixtures`).
 */
import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'

import {PUBLIC_BSKY_SERVICE} from '#/lib/constants'
import {type ModulePlay} from '#/lib/moduleLeaderboard'
import {createQueryKey} from '#/state/queries/util'
import {useAgent} from '#/state/session'

const capsulePlaysQueryKeyRoot = 'capsulePlays'

export const createCapsulePlaysQueryKey = (capsuleUris: readonly string[]) =>
  createQueryKey(capsulePlaysQueryKeyRoot, {capsuleUris})

/** One play as the AppView lists it, before it has been cleaned up. */
type PlayView = {
  capsuleUri?: unknown
  learnerDid?: unknown
  learnerHandle?: unknown
  learnerName?: unknown
  learnerAvatar?: unknown
  score?: unknown
  timeMs?: unknown
  /** When it was played, used to order a learner's own attempts. */
  at?: unknown
  /**
   * The level the learner played at, under either of the names a deployment uses.
   *
   * Read and passed through when it is there, and *not* invented when it is not:
   * the peer strip keeps a row it cannot file rather than dropping it, because a
   * missing level is a field nobody sent, not a learner from another year (see
   * `#/lib/moduleAudience`).
   */
  learnerLevel?: unknown
  level?: unknown
}

/**
 * A play the board can read, or `null` for one it cannot.
 *
 * A row without a learner cannot be attributed to anybody, and a board that
 * printed one as "someone" would be less useful than the same board without it —
 * so it is dropped rather than guessed at. A row that names no capsule is dropped
 * for the same reason: the board is built by walking the *module* and looking
 * plays up against it, so a row with no subject is a play nothing can be asked
 * about. Some deployments answer per capsule without echoing the uri back, which
 * is why the caller's single-uri case can hand one in as the fallback.
 */
function modulePlayOf(view: PlayView, fallbackUri?: string): ModulePlay | null {
  const did = typeof view.learnerDid === 'string' ? view.learnerDid.trim() : ''
  const capsuleUri =
    typeof view.capsuleUri === 'string' && view.capsuleUri
      ? view.capsuleUri
      : (fallbackUri ?? '')
  if (!did || !capsuleUri) return null
  return {
    capsuleUri,
    learnerDid: did,
    learnerHandle:
      typeof view.learnerHandle === 'string' ? view.learnerHandle : undefined,
    learnerName:
      typeof view.learnerName === 'string' ? view.learnerName : undefined,
    learnerAvatar:
      typeof view.learnerAvatar === 'string' ? view.learnerAvatar : undefined,
    score: typeof view.score === 'number' ? view.score : undefined,
    timeMs: typeof view.timeMs === 'number' ? view.timeMs : undefined,
    at: typeof view.at === 'string' ? view.at : undefined,
    learnerLevel: levelOf(view.learnerLevel ?? view.level),
  }
}

/** A level as a code worth carrying, or nothing. */
function levelOf(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined
}

/**
 * The plays of every capsule in a module, in one request.
 *
 * `capsuleUris` is the module's playlist, and the key is the sorted, de-duplicated
 * list rather than the order the playlist is in — so re-ordering a module does not
 * throw the board away and fetch it again. A single uri is also the shape a
 * one-capsule caller wants, and the rows are then read with that uri as the
 * fallback subject.
 */
export function useModulePlaysQuery(capsuleUris: readonly string[]) {
  const agent = useAgent()
  const stableUris = useMemo(
    () => Array.from(new Set(capsuleUris)).sort(),
    [capsuleUris],
  )
  const single = stableUris.length === 1 ? stableUris[0] : undefined

  return useQuery({
    queryKey: createCapsulePlaysQueryKey(stableUris),
    enabled: stableUris.length > 0,
    queryFn: async (): Promise<ModulePlay[]> => {
      const accessJwt = agent.session?.accessJwt
      if (!accessJwt) throw new Error('Not authenticated')

      const params = new URLSearchParams({limit: '100'})
      for (const uri of stableUris) params.append('capsuleUri', uri)

      const res = await fetch(
        `${PUBLIC_BSKY_SERVICE}/xrpc/app.bsky.feed.getCapsulePlays?${params}`,
        {headers: {Authorization: `Bearer ${accessJwt}`}},
      )
      if (!res.ok) {
        // 501 = the method exists but is not implemented; 404 = the server has
        // never heard of it. Neither is worth a retry storm or a console error —
        // report "no plays", which the board already knows how to say.
        if (res.status === 501 || res.status === 404) return []
        const err = (await res.json().catch(() => ({}))) as {message?: string}
        throw new Error(err.message ?? `HTTP ${res.status}`)
      }
      const data = (await res.json()) as {plays?: PlayView[]}
      return (data.plays ?? [])
        .map(view => modulePlayOf(view, single))
        .filter((play): play is ModulePlay => play !== null)
    },
    // Short: a class that has just been set the module comes back with new plays
    // during the lesson, and a stale board is worse than a small refetch.
    staleTime: 1000 * 30,
  })
}
